import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { IsNewDirective } from '../../../../directives/is-new.directive';
import { PaginationComponent } from '../../../core/components/pagination/pagination.component';
import { SortComponent } from '../../../core/components/sort/sort.component';
import { DatepickerComponent } from '../../../core/components/datepicker/datepicker.component';
import { SearchComponent } from '../../../core/components/search/search.component';
import { EventLogsFilterComponent } from '../../../core/components/table/filter-panel/filters/event-logs-filter/event-logs-filter.component';
import { DateRangeInput } from '../../../core/models/date-range-input';
import { processDateRange } from '../../../../helper/date-utils';
import { generateFileNameAppendix } from '../../../../helper/download-filename';
import { AuthService } from '../../../../services/auth.service';
import { FileService } from '../../../../services/file.service';
import { getAlertColor } from '../../../events-alarms/alert-colors';
import { Client, MeterEventLogDataOut, MeterEventLogQueryDataIn, PageInfo, PermissionsEnum, SortingType } from '../../../../api/api-reference';

@Component({
  selector: 'app-event-logs',
  standalone: true,
  imports: [CommonModule, TranslateModule, AngularSvgIconModule, IsNewDirective, PaginationComponent, SortComponent, DatepickerComponent, EventLogsFilterComponent, SearchComponent],
  templateUrl: './event-logs.component.html',
  styleUrl: './event-logs.component.scss',
})
export class EventLogsComponent implements OnInit {
  @Input() meterId = '';
  public PermissionsEnum = PermissionsEnum;

  events: MeterEventLogDataOut[] = [];
  // The current meter model's names for the logs, for the filter; rows carry the name of the model they were read under.
  labels: string[] = [];
  numberOfEvents = 0;
  lastPageSize = 1;
  pageSize = 10;
  currentPage = 1;

  requestData = new MeterEventLogQueryDataIn({
    meterId: '', // set from the input on every load
    pageInfo: new PageInfo({ pageSize: 10, page: 1 }),
    startDate: undefined,
    endDate: undefined,
    eventLogNumbers: [],
    search: undefined,
    sorting: SortingType.Descending,
  });

  isDatepickerOpen = false;
  startDate?: Date;
  endDate?: Date;
  selectedDate: string | null = '';

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private fileService: FileService,
    public authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.loadEvents(1);
    this.client.getMeterEventLogLabels(this.meterId).subscribe({
      next: (labels) => (this.labels = labels),
      error: (err) => this.toastr.error(err),
    });
  }

  loadEvents(page: number = 1): void {
    this.currentPage = page;
    this.requestData.meterId = this.meterId;
    this.requestData.pageInfo!.page = page;
    this.requestData.pageInfo!.pageSize = this.pageSize;
    this.client.getMeterEventLogs(this.requestData).subscribe({
      next: (response) => {
        this.events = response.data ?? [];
        this.numberOfEvents = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfEvents / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  // The six registers follow the IDIS event log order, so each log number has a fixed name and colour.
  eventLogLabelKey(eventLogNumber?: number): string {
    return `EVENTLOG_TYPE_${eventLogNumber}`;
  }

  eventLogColor(eventLogNumber?: number): string {
    return getAlertColor(`EventLog${eventLogNumber}`);
  }

  // Downloads every event matching the current sort, date range, event types and search, not just the page on screen.
  download() {
    const fileNameApendix = generateFileNameAppendix();
    this.requestData.meterId = this.meterId;

    this.client.downloadMeterEventLogs(this.requestData).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `EventLogs${fileNameApendix}`,
        );
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  pageChange(page: number) {
    this.loadEvents(page);
  }

  toggleDatepicker() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  onFilterApplied(eventLogNumbers: number[]): void {
    this.requestData.eventLogNumbers = eventLogNumbers;
    this.loadEvents(1);
  }

  searchChange(searchText: string): void {
    this.requestData.search = searchText || undefined;
    this.loadEvents(1);
  }

  onSortOption(option: string) {
    this.requestData.sorting = option === 'ASCENDING' ? SortingType.Ascending : SortingType.Descending;
    this.loadEvents(1);
  }

  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);
    this.startDate = result.startDate;
    this.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.loadEvents(1);
  }

  clearDates(): void {
    this.startDate = undefined;
    this.endDate = undefined;
    this.selectedDate = '';
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.loadEvents(1);
  }
}
